import { Observation as GraphQLObservation } from "@acto/gql/graphql";
import ObservationStatus from "./ObservationStatus";
import LocalizedStringMap from "./LocalizedStringMap";
import Customer, { type ClientForCustomer } from "./Customer";
import Employee from "./Employee";

/**
 * Observation shape consumed by the mappers below. Matches the generated `Observation`
 * except `client`, relaxed to {@link ClientForCustomer} so the partial `lastOrder`
 * projection in the client fragments stays assignable.
 */
type ObservationForDomain = Omit<GraphQLObservation, "client"> & {
  client: ClientForCustomer;
};

export default class Observation {
  constructor(
    public readonly id: string,
    private readonly title: LocalizedStringMap,
    private readonly description: LocalizedStringMap,
    public readonly status: ObservationStatus,
    public readonly processedReason: string | undefined,
    public readonly client: Customer,
    public readonly assignedEmployee: { id: string; name: string } | undefined,
    public readonly processedAt?: Date,
  ) {}

  public getLocalizedTitle(locale: string): string {
    return this.title.getTranslation(locale);
  }

  public getLocalizedDescription(locale: string): string {
    return this.description.getTranslation(locale);
  }

  static fromGraphql(data: ObservationForDomain): Observation {
    const client = Customer.fromGraphql(data.client);
    return Observation.createObservation(data, client);
  }

  static fromGraphqlForCustomer(data: ObservationForDomain, customer: Customer): Observation {
    return Observation.createObservation(data, customer);
  }

  private static createObservation(data: ObservationForDomain, customer: Customer): Observation {
    const titleMap = LocalizedStringMap.fromGraphql(data.title);
    const descriptionMap = LocalizedStringMap.fromGraphql(data.description);

    const observation = new Observation(
      data.id,
      titleMap,
      descriptionMap,
      ObservationStatus.fromGraphql(data.status),
      data.processedReason ?? undefined,
      customer,
      data.assignedEmployee ? Employee.fromGraphql(data.assignedEmployee) : undefined,
      (data.processedAt && new Date(data.processedAt)) ?? undefined,
    );

    customer.setObservation(observation);

    return observation;
  }
}
